import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { setHeaderLogoUrl, updateProductMetaData } from "@containers/App/reducer"
import { DEFAULT_ERROR_MESSAGE } from "@containers/App/constants"
import { parseAddressData, parseConfirmBookingError, parseLeadAddress, parseResponseData } from "./parser"
import { COMMON_ERROR_CONFIG, LINK_EXPIRED_ERROR_CONFIG } from "../constants"
const initialState = {
    isFetching: false,
    isFetched: false,
    data: null,
    error: null,
    addressDetails: {
        data: null,
        isFetching: false,
        isFetched: false,
        error: null,
    },
    confirmOrder: {
        data: null,
        isLoading: false,
        error: null,
    },
}

export const selfServeLeadPageSlice = createSlice({
    name: "selfServeLeadPageReducer",
    initialState,
    reducers: {
        clearConfirmBookingError(state) {
            state.confirmOrder = initialState.confirmOrder
        },
    },
    extraReducers: (builder) => {
        //handle asynchronous actions
        builder.addCase(fetchLeadPageData.fulfilled, (state, action) => {
            state.data = parseResponseData(action?.payload)
            state.isFetched = true
            state.isFetching = false
            state.error = false
        }),
            builder.addCase(fetchLeadPageData.pending, (state) => {
                state.isFetching = true
                state.error = null
                state.data = null
            }),
            builder.addCase(fetchLeadPageData.rejected, (state, action) => {
                state.isFetching = false
                state.isFetched = true
                state.data = null
                if (action.payload?.status_code === 410) {
                    state.error = LINK_EXPIRED_ERROR_CONFIG
                } else {
                    state.error = COMMON_ERROR_CONFIG
                }
            })
        builder.addCase(fetchLeadAddress.fulfilled, (state, action) => {
            state.addressDetails.data = parseLeadAddress(action?.payload, action.meta.arg.isRehash)
            state.addressDetails.isFetched = true
            state.addressDetails.isFetching = false
            state.addressDetails.error = false
        }),
            builder.addCase(fetchLeadAddress.pending, (state) => {
                state.addressDetails.isFetching = true
                state.addressDetails.error = null
                state.addressDetails.data = null
            }),
            builder.addCase(fetchLeadAddress.rejected, (state, action) => {
                state.addressDetails.isFetching = false
                state.addressDetails.data = null
                state.addressDetails.error = action.payload
            })

        builder.addCase(updateLeadAddress.fulfilled, (state, action) => {
            const { key, addressObj, errorObj } = parseAddressData(action?.payload)
            const pageData = { ...state.data }
            const addressWidget = pageData.widgets.find((widget) => widget.key === key)
            const slotWidget = pageData.widgets.find((widget) => widget.key === "test_slot")
            if (addressWidget) {
                addressWidget.data.addressDetails = { ...addressWidget.data.addressDetails, ...addressObj }
                addressWidget.overviewInfo.description = [
                    addressWidget.data?.addressDetails.street || "",
                    addressWidget.data?.addressDetails.city || "",
                    addressWidget.data?.addressDetails.state || "",
                    addressWidget.data?.addressDetails.pincode || "",
                ].join(" ")
                if (errorObj) {
                    addressWidget.stepInfo.error = { ...addressWidget.stepInfo.error, ...errorObj }
                    addressWidget.data.cta = { ...addressWidget.data.cta, isShow: true }
                    slotWidget.overviewInfo.link = { ...slotWidget.overviewInfo.link, isDisabled: true }
                } else {
                    slotWidget.overviewInfo.link = { ...slotWidget.overviewInfo.link, isDisabled: false }
                    addressWidget.data.cta = {
                        ...addressWidget.data.cta,
                        isShow: !pageData.flags.isEditAddressDisabled,
                    }
                    addressWidget.stepInfo.error = {}
                }
            }
            state.data = pageData
        })
        builder.addCase(confirmOrderBooking.fulfilled, (state, action) => {
            action.meta.arg.navigate(action.payload?.redirect_url)
            return { ...initialState }
        }),
            builder.addCase(confirmOrderBooking.pending, (state) => {
                state.confirmOrder.isLoading = true
                state.confirmOrder.error = null
            }),
            builder.addCase(confirmOrderBooking.rejected, (state, action) => {
                state.confirmOrder.isLoading = false
                state.confirmOrder.error = parseConfirmBookingError(action.payload)
            })
    },
})

export const confirmOrderBooking = createAsyncThunk(
    "selfServeLeadPageReducer/confirmOrderBooking",
    async (_arg, { extra, rejectWithValue }) => {
        const { slotDetails } = _arg
        const { ppmcApi } = extra
        const payload = {
            slot: slotDetails
                ? {
                      slot_id: slotDetails.slotId,
                      label: slotDetails.label,
                      epoch_slot: slotDetails.epochSlot,
                      slot_index: slotDetails.slotIndex,
                  }
                : null,
        }
        try {
            const res = await ppmcApi.post("/v6/self-serve/order", payload)
            return Promise.resolve(res.data)
        } catch (error) {
            return rejectWithValue(error)
        }
    }
)

export const fetchLeadPageData = createAsyncThunk(
    "selfServeLeadPageReducer/fetchData",
    async (_arg, { extra, rejectWithValue, dispatch }) => {
        const { ppmcApi } = extra
        try {
            const res = await ppmcApi.get("v6/self-serve/lead")

            if (res?.data?.meta_data) {
                dispatch(
                    updateProductMetaData({
                        title: res.data.meta_data?.title,
                        description: res.data.meta_data?.description,
                    })
                )
                if (res.data.meta_data.logo?.url) {
                    dispatch(setHeaderLogoUrl(res.data?.meta_data?.logo?.url))
                }
            }
            return Promise.resolve(res.data)
        } catch (error) {
            return rejectWithValue(error)
        }
    }
)

export const fetchLeadAddress = createAsyncThunk(
    "selfServeLeadPageReducer/fetchLeadAddress",
    async (_arg, { extra, rejectWithValue, fulfillWithValue }) => {
        const { ppmcApi } = extra
        try {
            const res = await ppmcApi.get("v6/self-serve/lead/address")
            return fulfillWithValue(res.data)
        } catch (error) {
            return rejectWithValue(error?.message || error?.error?.message || DEFAULT_ERROR_MESSAGE)
        }
    }
)

export const updateLeadAddress = createAsyncThunk(
    "selfServeLeadPageReducer/updateLeadAddress",
    async (_arg, { extra, rejectWithValue }) => {
        const { ppmcApi } = extra
        const { payload } = _arg
        try {
            const selfServePayload = {
                line_1: payload.street1,
                line_2: payload.street2,
                line_3: payload.street3,
                city: payload.city,
                state: payload.state,
                country: payload.country,
                pincode: `${payload.pincode}`,
                name: payload.name,
                phone_number: payload.contact_number,
            }
            if (payload.geodata_latitude && payload.geodata_longitude) {
                selfServePayload.location = {
                    type: "Point",
                    coordinates: [payload.geodata_latitude, payload.geodata_longitude],
                }
            }
            const res = await ppmcApi.put("v6/self-serve/lead/details", selfServePayload)
            return Promise.resolve(res.data)
        } catch (error) {
            return rejectWithValue(error?.message || error?.error?.message || true)
        }
    }
)

export const { clearConfirmBookingError } = selfServeLeadPageSlice.actions

export const selfServeLeadPageReducer = selfServeLeadPageSlice.reducer
